import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import type {Dayjs} from 'dayjs';
import {dayjs, tzLabel, DEFAULT_TIMEZONE} from 'src/utils/time';
import {amenityServiceGetAvailabilityRange, reservationServiceFind} from 'src/apis/autoGenerate/services';
import {DayAvailability} from 'src/apis/autoGenerate/types/amenity';
import {Reservation} from 'src/apis/autoGenerate/types/reservation';

interface Props {
  amenityId: string;
  openHours: {start: string; end: string};
  duration: number;
  capacity: number;
  /** IANA timezone of the parent apartment. Falls back to default US/Eastern. */
  apartmentTimeZone?: string;
}

interface SlotInfo {
  date: string;
  timeLabel: string;
  startTime: string;
  endTime: string;
  currentBookings: number;
  capacity: number;
  available: boolean;
  isPast: boolean;
}

export default function WeeklyScheduleCalendar({
  amenityId,
  openHours,
  duration,
  capacity,
  apartmentTimeZone,
}: Props) {
  const tz = apartmentTimeZone || DEFAULT_TIMEZONE;
  // Week starts on Monday in the apartment's local timezone, not browser-local.
  const [weekStart, setWeekStart] = useState<Dayjs>(() =>
    dayjs().tz(tz).startOf('week').add(1, 'day')
  );

  // Reset the visible week when the apartment timezone changes.
  useEffect(() => {
    setWeekStart(dayjs().tz(tz).startOf('week').add(1, 'day'));
  }, [tz]);
  const [availabilityData, setAvailabilityData] = useState<DayAvailability[]>([]);
  const [loading, setLoading] = useState(false);

  // Popover state
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  const [slotReservations, setSlotReservations] = useState<Reservation[]>([]);
  const [loadingReservations, setLoadingReservations] = useState(false);

  // Generate time labels from open hours + duration
  const timeSlots = useMemo(() => {
    const [startH, startM] = openHours.start.split(':').map(Number);
    const [endH, endM] = openHours.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    if (endMinutes === 0) endMinutes = 24 * 60;
    const slots: {startLabel: string; rangeLabel: string; startMinutes: number}[] = [];
    for (let m = startMinutes; m + duration <= endMinutes; m += duration) {
      const h = Math.floor(m / 60) % 24;
      const min = m % 60;
      const endTotal = m + duration;
      const endHLabel = Math.floor(endTotal / 60) % 24;
      const endMinLabel = endTotal % 60;
      const startLabel = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      const endLabel = `${String(endHLabel).padStart(2, '0')}:${String(endMinLabel).padStart(2, '0')}`;
      slots.push({
        startLabel,
        rangeLabel: `${startLabel}-${endLabel}`,
        startMinutes: m,
      });
    }
    return slots;
  }, [openHours, duration]);

  // 7 days of the week
  const weekDays = useMemo(() => {
    return Array.from({length: 7}, (_, i) => weekStart.add(i, 'day'));
  }, [weekStart]);

  // Fetch availability for the week
  const fetchAvailability = useCallback(async () => {
    if (!amenityId) return;
    setLoading(true);
    try {
      const startDate = weekStart.format('YYYY-MM-DD');
      const endDate = weekStart.add(6, 'day').format('YYYY-MM-DD');
      const res = await amenityServiceGetAvailabilityRange(amenityId, startDate, endDate);
      const data = (res as any)?.data;
      setAvailabilityData(data?.days || []);
    } catch {
      setAvailabilityData([]);
    } finally {
      setLoading(false);
    }
  }, [amenityId, weekStart]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Build lookup: date -> DayAvailability (index-based to avoid timezone issues)
  const dayMap = useMemo(() => {
    const map = new Map<string, DayAvailability>();
    for (const day of availabilityData) {
      map.set(day.date, day);
    }
    return map;
  }, [availabilityData]);

  // Get cell data. Slot times come from the backend (UTC ISO strings built in the
  // apartment's tz). We only fall back to client-side reconstruction in the
  // apartment's tz if the backend didn't return a matching slot.
  const getCellData = (date: Dayjs, timeLabel: string, slotIndex: number): SlotInfo => {
    const dateStr = date.format('YYYY-MM-DD');
    const day = dayMap.get(dateStr);
    const slot = day?.slots[slotIndex]; // index-based: same order as timeSlots
    const now = dayjs();

    const fallbackStart = dayjs.tz(`${dateStr} ${timeLabel}`, tz);
    const fallbackEnd = fallbackStart.add(duration, 'minute');

    const slotEndDayjs = slot ? dayjs.utc(slot.endTime) : fallbackEnd;
    const isPast = slotEndDayjs.isBefore(now);

    return {
      date: dateStr,
      timeLabel,
      startTime: slot?.startTime || fallbackStart.toISOString(),
      endTime: slot?.endTime || fallbackEnd.toISOString(),
      currentBookings: slot?.currentBookings ?? 0,
      capacity: slot?.capacity || capacity,
      available: slot?.available ?? true,
      isPast,
    };
  };

  // Cell color
  const getCellColor = (cell: SlotInfo) => {
    if (cell.isPast) return '#f5f5f5';
    if (cell.currentBookings === 0) return '#e8f5e9';
    if (cell.currentBookings >= cell.capacity) return '#ffebee';
    return '#fff3e0';
  };

  const getCellTextColor = (cell: SlotInfo) => {
    if (cell.isPast) return '#9e9e9e';
    if (cell.currentBookings === 0) return '#2e7d32';
    if (cell.currentBookings >= cell.capacity) return '#c62828';
    return '#e65100';
  };

  // Handle cell click -> show popover with reservations
  const handleCellClick = async (event: React.MouseEvent<HTMLElement>, cell: SlotInfo) => {
    if (cell.currentBookings === 0) return; // Nothing to show
    setPopoverAnchor(event.currentTarget);
    setSelectedSlot(cell);
    setLoadingReservations(true);
    try {
      const res = await reservationServiceFind(cell.date, amenityId);
      const data = (res as any)?.data;
      const allReservations: Reservation[] = data?.reservations || [];
      // Build slot boundaries in the apartment's timezone → UTC epoch ms.
      const slotStartMs = dayjs.tz(`${cell.date} ${cell.timeLabel}`, tz).valueOf();
      const slotEndMs = slotStartMs + duration * 60 * 1000;

      // Filter to active reservations that overlap the specific slot (UTC compare)
      const filtered = allReservations.filter(r => {
        if (r.status !== 'active') return false;
        const rStartMs = new Date(r.startTime!).getTime();
        const rEndMs = new Date(r.endTime!).getTime();
        return rStartMs < slotEndMs && rEndMs > slotStartMs;
      });
      setSlotReservations(filtered);
    } catch {
      setSlotReservations([]);
    } finally {
      setLoadingReservations(false);
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedSlot(null);
    setSlotReservations([]);
  };

  // Navigation -- "today" is relative to the apartment's timezone.
  const goToday = () => setWeekStart(dayjs().tz(tz).startOf('week').add(1, 'day'));
  const goPrev = () => setWeekStart(prev => prev.subtract(7, 'day'));
  const goNext = () => setWeekStart(prev => prev.add(7, 'day'));

  const weekLabel = `${weekStart.format('MMM D')} - ${weekStart.add(6, 'day').format('MMM D, YYYY')}`;

  return (
    <Box>
      {/* Navigation Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1.5}}>
        <Stack direction="column" spacing={0.25}>
          <Typography variant="subtitle2" color="text.secondary">Schedule</Typography>
          <Typography variant="caption" color="text.secondary">
            {(() => {
              const short = tzLabel(tz);
              return `Times in ${short ? `${short} (${tz})` : tz}`;
            })()}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button size="small" startIcon={<TodayIcon />} onClick={goToday} sx={{textTransform: 'none', mr: 1}}>
            Today
          </Button>
          <IconButton size="small" onClick={goPrev}><ChevronLeftIcon /></IconButton>
          <Typography variant="body2" sx={{minWidth: 170, textAlign: 'center', fontWeight: 500}}>
            {weekLabel}
          </Typography>
          <IconButton size="small" onClick={goNext}><ChevronRightIcon /></IconButton>
        </Stack>
      </Stack>

      {/* Calendar Grid */}
      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1}}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '72px repeat(7, 1fr)',
              minWidth: 600,
            }}
          >
            {/* Header row */}
            <Box sx={{
              borderBottom: '2px solid #e0e0e0',
              borderRight: '1px solid #e0e0e0',
              p: 0.5,
              bgcolor: '#fafafa',
            }} />
            {weekDays.map(day => {
              const isToday = day.isSame(dayjs().tz(tz), 'day');
              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  sx={{
                    borderBottom: '2px solid #e0e0e0',
                    borderRight: '1px solid #e0e0e0',
                    p: 0.75,
                    textAlign: 'center',
                    bgcolor: isToday ? '#e3f2fd' : '#fafafa',
                    '&:last-child': {borderRight: 'none'},
                  }}
                >
                  <Typography variant="caption" sx={{fontWeight: 600, fontSize: '0.7rem', color: isToday ? '#1565c0' : 'text.secondary'}}>
                    {day.format('ddd')}
                  </Typography>
                  <Typography variant="body2" sx={{fontWeight: isToday ? 700 : 400, fontSize: '0.8rem', color: isToday ? '#1565c0' : 'text.primary'}}>
                    {day.format('M/D')}
                  </Typography>
                </Box>
              );
            })}

            {/* Time slot rows */}
            {timeSlots.map((slot, slotIndex) => (
              <>
                {/* Time label */}
                <Box
                  key={`label-${slot.startLabel}`}
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    borderRight: '1px solid #e0e0e0',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap'}}
                  >
                    {slot.rangeLabel}
                  </Typography>
                </Box>

                {/* Day cells */}
                {weekDays.map(day => {
                  const cell = getCellData(day, slot.startLabel, slotIndex);
                  const bgColor = getCellColor(cell);
                  const textColor = getCellTextColor(cell);
                  const hasBookings = cell.currentBookings > 0;

                  return (
                    <Box
                      key={`${day.format('YYYY-MM-DD')}-${slot.startLabel}`}
                      onClick={e => hasBookings ? handleCellClick(e, cell) : undefined}
                      sx={{
                        borderBottom: '1px solid #f0f0f0',
                        borderRight: '1px solid #e0e0e0',
                        '&:last-child': {borderRight: 'none'},
                        bgcolor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 36,
                        cursor: hasBookings ? 'pointer' : 'default',
                        transition: 'opacity 0.15s',
                        '&:hover': hasBookings ? {opacity: 0.75} : {},
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: hasBookings ? 600 : 400,
                          color: textColor,
                        }}
                      >
                        {cell.currentBookings}/{cell.capacity}
                      </Typography>
                    </Box>
                  );
                })}
              </>
            ))}
          </Box>
        </Box>
      )}

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{mt: 1.5}} justifyContent="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: '#e8f5e9'}} />
          <Typography variant="caption" color="text.secondary">Available</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: '#fff3e0'}} />
          <Typography variant="caption" color="text.secondary">Partial</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: '#ffebee'}} />
          <Typography variant="caption" color="text.secondary">Full</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: '#f5f5f5'}} />
          <Typography variant="caption" color="text.secondary">Past</Typography>
        </Stack>
      </Stack>

      {/* Slot Detail Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        transformOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Box sx={{p: 2, minWidth: 320, maxWidth: 420}}>
          {selectedSlot && (() => {
            // Compute end time label from start label + duration (avoids timezone conversion)
            const [sh, sm] = selectedSlot.timeLabel.split(':').map(Number);
            const endMinutes = sh * 60 + sm + duration;
            const endH = Math.floor(endMinutes / 60);
            const endMin = endMinutes % 60;
            const endLabel = `${String(endH).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
            return (
              <>
                <Typography variant="subtitle2" sx={{mb: 1}}>
                  {dayjs(selectedSlot.date).format('MMM D (ddd)')} {selectedSlot.timeLabel} - {endLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
                  {selectedSlot.currentBookings} / {selectedSlot.capacity} booked
                </Typography>
                {loadingReservations ? (
                  <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                    <CircularProgress size={20} />
                  </Box>
                ) : slotReservations.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{py: 0.5, fontSize: '0.75rem'}}>User</TableCell>
                        <TableCell sx={{py: 0.5, fontSize: '0.75rem'}}>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {slotReservations.map(r => {
                        // Display reservation times in the apartment's tz.
                        const fmt = (iso?: string) =>
                          iso ? dayjs.utc(iso).tz(tz).format('HH:mm') : '-';
                        return (
                          <TableRow key={r.id}>
                            <TableCell sx={{py: 0.5, fontSize: '0.75rem'}}>{r.userName || r.userEmail || '-'}</TableCell>
                            <TableCell sx={{py: 0.5, fontSize: '0.75rem'}}>
                              {fmt(r.startTime)}-{fmt(r.endTime)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No active reservations for this slot</Typography>
                )}
              </>
            );
          })()}
        </Box>
      </Popover>
    </Box>
  );
}
